import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { VendorTile } from '../components/ui/VendorTile';
import { Button } from '../components/ui/Button';
import { FieldInput, FieldSelect } from '../components/ui/FieldInput';
import { Plus, X, Sparkle } from '../components/ui/Icons';

export function Rules() {
  const isMobile = useIsMobile();
  const [rules, setRules] = useState<Array<{ id: string; vendor_pattern: string; account_code: string }>>([]);
  const [accounts, setAccounts] = useState<Array<{ AccountID: string; Code: string; Name: string; Type: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [newVendor, setNewVendor] = useState('');
  const [newAccount, setNewAccount] = useState('');

  async function load() {
    const [r, a] = await Promise.all([api.listCategoryRules(), api.xeroAccounts()]);
    setRules(r.rules);
    setAccounts(a.accounts.filter((ac) => ac.Type === 'EXPENSE' || ac.Type === 'DIRECTCOSTS'));
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function addRule() {
    if (!newVendor.trim() || !newAccount) return;
    await api.createCategoryRule({ vendor_pattern: newVendor.trim(), account_code: newAccount });
    setNewVendor('');
    setNewAccount('');
    await load();
  }

  async function deleteRule(id: string) {
    await api.deleteCategoryRule(id);
    setRules((prev) => prev.filter((r) => r.id !== id));
  }

  if (loading) {
    return <div style={{ padding: 40, color: T.graphiteDim, fontSize: 14 }}>Loading...</div>;
  }

  const accountMap = new Map(accounts.map((a) => [a.Code, a]));

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ ...sx.serif, fontSize: 28, color: T.graphite, margin: 0, marginBottom: 6 }}>
            Category rules
          </h1>
          <span style={{ ...sx.mono, fontSize: 16, fontWeight: 600, color: T.graphite }}>
            {rules.length}
          </span>
          <span style={{ ...sx.labelLight, marginLeft: 6 }}>active rules</span>
        </div>

        {/* Rules list */}
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 14,
            overflow: 'hidden',
            marginBottom: 16,
          }}
        >
          {rules.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: T.graphiteDim, fontSize: 14 }}>
              No rules yet. Add one below.
            </div>
          ) : (
            rules.map((r, i) => {
              const acct = accountMap.get(r.account_code);
              return (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderTop: i > 0 ? `1px solid ${T.paperLine}` : 'none',
                  }}
                >
                  <VendorTile vendor={r.vendor_pattern} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: T.graphite }}>
                      {r.vendor_pattern}
                    </div>
                    <div style={{ ...sx.mono, fontSize: 12, color: T.graphiteDim }}>
                      {r.account_code}{acct ? ` - ${acct.Name}` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteRule(r.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 8,
                      color: T.graphiteDim,
                      minWidth: 44,
                      minHeight: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Delete rule"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Auto-learned note */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            background: T.paper2,
            borderRadius: 10,
            border: `1px solid ${T.paperLine}`,
            marginBottom: 24,
          }}
        >
          <Sparkle size={14} color={T.graphiteDim} />
          <span style={{ fontSize: 13, color: T.graphiteDim }}>
            Rules are also auto-learned when you correct the AI's account selection.
          </span>
        </div>

        {/* Add rule form */}
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 14,
            padding: 20,
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600, color: T.graphite, margin: 0, marginBottom: 16 }}>
            Add rule
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldInput
              label="Vendor pattern"
              value={newVendor}
              onChange={setNewVendor}
              placeholder="e.g. Uber, AWS, Bunnings"
            />
            {accounts.length > 0 ? (
              <FieldSelect
                label="Account"
                value={newAccount}
                onChange={setNewAccount}
                options={accounts.map((a) => ({ value: a.Code, label: `${a.Code} - ${a.Name}` }))}
                placeholder="-- Select account --"
              />
            ) : (
              <FieldInput
                label="Account code"
                value={newAccount}
                onChange={setNewAccount}
                placeholder="e.g. 420"
              />
            )}
            <Button
              variant="sage"
              onClick={addRule}
              disabled={!newVendor.trim() || !newAccount}
            >
              <Plus size={15} /> Add rule
            </Button>
          </div>
        </div>

        {/* Safe area padding for iOS */}
        <div style={{ height: 'env(safe-area-inset-bottom, 20px)' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ ...sx.serif, fontSize: 32, color: T.graphite, margin: 0, marginBottom: 8 }}>
            Category rules
          </h1>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <span style={{ ...sx.mono, fontSize: 20, fontWeight: 600, color: T.graphite }}>{rules.length}</span>
              <span style={{ ...sx.labelLight, marginLeft: 6 }}>active rules</span>
            </div>
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
        {/* Rules table */}
        <div>
          <div
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.paperLine}`,
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            {/* Table header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr 1fr 32px',
                gap: 12,
                padding: '10px 16px',
                background: T.paper2,
                borderBottom: `1px solid ${T.paperLine}`,
              }}
            >
              <span />
              <span style={{ ...sx.labelLight }}>Vendor pattern</span>
              <span style={{ ...sx.labelLight }}>Account</span>
              <span />
            </div>

            {rules.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: T.graphiteDim, fontSize: 14 }}>
                No rules yet. Add one to get started.
              </div>
            ) : (
              rules.map((r, i) => {
                const acct = accountMap.get(r.account_code);
                return (
                  <div
                    key={r.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '36px 1fr 1fr 32px',
                      gap: 12,
                      padding: '12px 16px',
                      alignItems: 'center',
                      borderTop: i > 0 ? `1px solid ${T.paperLine}` : 'none',
                    }}
                  >
                    <VendorTile vendor={r.vendor_pattern} size={36} />
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 500, color: T.graphite }}>
                        {r.vendor_pattern}
                      </span>
                    </div>
                    <div>
                      <span style={{ ...sx.mono, fontSize: 13, color: T.graphite }}>
                        {r.account_code}
                      </span>
                      {acct && (
                        <span style={{ fontSize: 13, color: T.graphiteDim, marginLeft: 6 }}>
                          {acct.Name}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => deleteRule(r.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        color: T.graphiteDim,
                        opacity: 0.4,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = T.fail; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; e.currentTarget.style.color = T.graphiteDim; }}
                      title="Delete rule"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Auto-learned note */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 16,
              padding: '12px 16px',
              background: T.paper2,
              borderRadius: 10,
              border: `1px solid ${T.paperLine}`,
            }}
          >
            <Sparkle size={14} color={T.graphiteDim} />
            <span style={{ fontSize: 13, color: T.graphiteDim }}>
              Rules are also auto-learned when you correct the AI's account selection.
            </span>
          </div>
        </div>

        {/* Add rule form */}
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 12,
            padding: 24,
            alignSelf: 'start',
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600, color: T.graphite, margin: 0, marginBottom: 20 }}>
            Add rule
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldInput
              label="Vendor pattern"
              value={newVendor}
              onChange={setNewVendor}
              placeholder="e.g. Uber, AWS, Bunnings"
            />
            {accounts.length > 0 ? (
              <FieldSelect
                label="Account"
                value={newAccount}
                onChange={setNewAccount}
                options={accounts.map((a) => ({ value: a.Code, label: `${a.Code} - ${a.Name}` }))}
                placeholder="-- Select account --"
              />
            ) : (
              <FieldInput
                label="Account code"
                value={newAccount}
                onChange={setNewAccount}
                placeholder="e.g. 420"
              />
            )}
            <Button
              variant="sage"
              onClick={addRule}
              disabled={!newVendor.trim() || !newAccount}
            >
              <Plus size={15} /> Add rule
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
